import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * FactionRelationsPattern - Faction diplomacy grid with relation tracking
 *
 * Config properties used:
 * - relationsKey: character/campaign key for persisted relations (default: 'factionRelations')
 * - nations: array of nation IDs (read profiles from frameStaticData)
 * - relationScale: { min, max } (default: -3 to +3)
 * - showAssets: show nation assets section
 * - showProblems: show nation problems section
 * - showObjectives: show nation objectives section
 */
const RELATION_LABELS = {
  '-3': 'Nemeses',
  '-2': 'Enemies',
  '-1': 'Unfriendly',
  '0': 'Neutral',
  '1': 'Friendly',
  '2': 'Allies',
  '3': 'Close Allies',
};

const RELATION_COLORS = {
  '-3': 'bg-red-700 text-red-100',
  '-2': 'bg-red-500/80 text-red-100',
  '-1': 'bg-orange-500/70 text-orange-100',
  '0': 'bg-gray-500/60 text-gray-100',
  '1': 'bg-sky-500/70 text-sky-100',
  '2': 'bg-green-500/70 text-green-100',
  '3': 'bg-green-700 text-green-100',
};

const FactionRelationsPattern = ({
  config,
  character,
  updateCharacter,
  frameStaticData,
  sessionState,
  setSessionState,
  onClose,
}) => {
  const relationsKey = config?.relationsKey || 'factionRelations';
  const nationIds = config?.nations || [];
  const scale = config?.relationScale || { min: -3, max: 3 };
  const nations = frameStaticData?.nations || {};

  // Labels from static data or fallback
  const labels = frameStaticData?.customMechanics?.factionRelations?.scale?.labels || RELATION_LABELS;

  // Current relations from character (persisted) — keyed as "nationA-nationB"
  const relations = character?.[relationsKey] || {};

  // Expanded nation detail panel
  const [expandedNation, setExpandedNation] = useState(null);

  /** Get the relation key for a pair (alphabetical order for consistency) */
  const pairKey = (a, b) => [a, b].sort().join('-');

  /** Get current relation value for a pair */
  const getRelation = (a, b) => {
    const key = pairKey(a, b);
    if (relations[key] !== undefined) return relations[key];
    // Fall back to starting relations from static data
    return nations[a]?.startingRelations?.[b] ?? 0;
  };

  /** Set relation for a pair */
  const setRelation = (a, b, value) => {
    const clamped = Math.max(scale.min, Math.min(scale.max, value));
    const key = pairKey(a, b);
    const newRelations = { ...relations, [key]: clamped };
    updateCharacter({ ...character, [relationsKey]: newRelations });

    const nameA = nations[a]?.name || a;
    const nameB = nations[b]?.name || b;
    const label = labels[String(clamped)] || clamped;
    toast.success(`${nameA} ↔ ${nameB}: ${label} (${clamped > 0 ? '+' : ''}${clamped})`, { icon: '⚔️' });
  };

  /** Reset all relations to starting values */
  const handleReset = () => {
    updateCharacter({ ...character, [relationsKey]: {} });
    toast.success('Relations reset to starting values', { icon: '🔄' });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-indigo-600 dark:text-indigo-400 mb-1">
          ⚔️ Faction Relations
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Track diplomatic relations between the five nations of Althas
        </p>
      </div>

      {/* Relation Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="p-1" />
              {nationIds.map(id => (
                <th key={id} className="p-1 text-center font-medium text-gray-300 truncate max-w-[70px]">
                  {(nations[id]?.name || id).slice(0, 3).toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nationIds.map(rowId => (
              <tr key={rowId}>
                <td className="p-1 font-medium text-gray-300 truncate max-w-[80px]">
                  {nations[rowId]?.name || rowId}
                </td>
                {nationIds.map(colId => {
                  if (rowId === colId) {
                    return <td key={colId} className="p-1 text-center text-gray-600">--</td>;
                  }
                  const val = getRelation(rowId, colId);
                  const colorClass = RELATION_COLORS[String(val)] || RELATION_COLORS['0'];
                  return (
                    <td key={colId} className="p-1">
                      <div className="flex items-center justify-center gap-2">
                        <DomainCardButton
                          onClick={() => setRelation(rowId, colId, val - 1)}
                          variant={val <= scale.min ? 'disabled' : 'secondary'}
                          className="px-2 py-1"
                        >
                          -
                        </DomainCardButton>
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold min-w-[28px] text-center ${colorClass}`}>
                          {val > 0 ? `+${val}` : val}
                        </span>
                        <DomainCardButton
                          onClick={() => setRelation(rowId, colId, val + 1)}
                          variant={val >= scale.max ? 'disabled' : 'secondary'}
                          className="px-2 py-1"
                        >
                          +
                        </DomainCardButton>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 justify-center">
        {Object.entries(labels).map(([val, label]) => (
          <span
            key={val}
            className={`text-[10px] px-1.5 py-0.5 rounded ${RELATION_COLORS[val] || 'bg-gray-600 text-gray-200'}`}
          >
            {val > 0 ? `+${val}` : val} {label}
          </span>
        ))}
      </div>

      {/* Nation Profiles */}
      <div className="space-y-2">
        <h4 className="font-medium text-gray-300 text-sm">Nation Profiles</h4>
        {nationIds.map(id => {
          const nation = nations[id];
          if (!nation) return null;
          const isExpanded = expandedNation === id;
          return (
            <div key={id} className="bg-gray-800 rounded-lg overflow-hidden">
              <DomainCardButton
                onClick={() => setExpandedNation(isExpanded ? null : id)}
                variant="secondary"
                className="w-full px-3 py-1 flex items-center justify-between"
              >
                <span className="font-medium text-gray-200">{nation.name}</span>
                <span className="text-gray-500 text-xs">{isExpanded ? '▲' : '▼'}</span>
              </DomainCardButton>
              {isExpanded && (
                <div className="px-3 pb-3 space-y-2 text-sm text-gray-400">
                  <p className="italic">{nation.description}</p>
                  {config?.showAssets && nation.assets?.length > 0 && (
                    <div>
                      <span className="text-green-400 font-medium">Assets: </span>
                      {nation.assets.join(', ')}
                    </div>
                  )}
                  {config?.showProblems && nation.problems?.length > 0 && (
                    <div>
                      <span className="text-red-400 font-medium">Problems: </span>
                      {nation.problems.join(', ')}
                    </div>
                  )}
                  {config?.showObjectives && (
                    <>
                      {nation.majorObjective && (
                        <div>
                          <span className="text-yellow-400 font-medium">Major Objective: </span>
                          {nation.majorObjective}
                        </div>
                      )}
                      {nation.minorObjectives?.length > 0 && (
                        <div>
                          <span className="text-yellow-300 font-medium">Minor: </span>
                          {nation.minorObjectives.join('; ')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <DomainCardButton onClick={handleReset} variant="warning" className="w-full">
        Reset to Starting Relations
      </DomainCardButton>
      <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
        Close
      </DomainCardButton>
    </div>
  );
};

export default FactionRelationsPattern;
